/**
 * Data2Dollars Energy Asset OS — local assistant engine.
 * Deal-specific answers may reference the loaded package (e.g. Eagle); the product is not Eagle VDR.
 */
import { formatCurrency, formatInt, formatPct } from './format.js';
import {
  ABS_NOTE_BASE_LABEL,
  ABS_NOTE_UNSCALED_LABEL,
  deckPricePhrase,
  noteCasePointer,
  packageDeckInputs,
} from '../meeting/model.js';

function moneyMM(n) {
  const v = Number(n);
  return Number.isFinite(v) ? `$${v.toFixed(1)}MM` : null;
}

/** Offline answer: stated ask and our bid stay separate. */
export function localSellerAskAnswer(d) {
  const ask = d?.sellerAskMM;
  const bid = d?.deskBid;
  const bidMoney = moneyMM(bid?.bidPriceMM);
  const source = bid?.source === 'edited' ? 'edited' : 'default';
  const caseName = d?.livePageState?.case;
  const caseBit = caseName ? `, ${caseName}` : '';
  const bidLine = bidMoney
    ? `Our bid is ${bidMoney} (${source}${caseBit}).`
    : 'Our bid is not set on this page.';
  if (ask == null || ask === '') {
    return `No stated seller ask on this package. ${bidLine} That bid is ours, not the seller's ask.`;
  }
  return `Seller ask is ${moneyMM(ask)}. ${bidLine} The seller ask and our bid are different figures.`;
}

export function localNoteSizingAnswer(context) {
  const live = context?.livePageState;
  const deck = deckPricePhrase(packageDeckInputs(context?.dealPackage));
  const stripPrices = live?.baseStrip;
  const strip = deckPricePhrase(stripPrices);
  const analysis = deck
    ? `Analysis shows **${ABS_NOTE_BASE_LABEL}** (${deck}).`
    : `Analysis shows **${ABS_NOTE_BASE_LABEL}**.`;
  const finance = strip
    ? `Finance shows **${ABS_NOTE_UNSCALED_LABEL}** (${strip}).`
    : `Finance shows **${ABS_NOTE_UNSCALED_LABEL}**.`;
  const financeNote = live?.financeNoteMM ?? live?.maxNoteSizeMM;
  return [
    '**ABS note sizing**',
    analysis,
    finance,
    noteCasePointer('analysis', financeNote, { strip: stripPrices }),
    noteCasePointer('finance', live?.analysisNoteMM, { strip: stripPrices }),
    'They match when the package deck is already the base strip. A lower forecast on Value is not a second note size.',
  ].join('\n\n');
}

export const SUGGESTED_QUESTIONS = {
  screen: [
    'Which deals fall outside the ARO vs PDP PV criterion, and why?',
    'Rank the deals by ABS note size and name the binding constraint.',
    'What data would move the "needs review" deals to meets criteria?',
    'Summarize the top risk for each deal in one line.',
  ],
  decision: [
    'What IRR do I get at a $13MM bid?',
    'How should we frame Boswell concentration for IC?',
    'What does the stress case imply for bid ceiling?',
    'Walk the price → ARO → IRR bridge.',
  ],
  executive: [
    "What's the top-line summary?",
    'How concentrated is value in Boswell?',
    'What are the three biggest risks?',
    'Walk me through the bid timeline.',
  ],
  reserves: [
    'Summarize PDP vs PBP reserves.',
    'What price deck and differentials are assumed?',
    'Which wells dominate PV10?',
    'How does PV change at 15% and 20%?',
  ],
  cashflow: [
    'How does L12M cash flow compare to NTM?',
    'Why is realized price so far below HH?',
    'What drives LOE?',
    'How volatile is monthly NOCF?',
  ],
  wells: [
    'How many wells are PDP vs PDSI vs P&A?',
    'Which wells have negative PV10?',
    'What are the behind-pipe candidates?',
    'Tell me about Boswell.',
  ],
  well: [
    'Summarize this well for IC in five bullets.',
    'How does seller PV10 compare to the live model?',
    'What production / decline risk should we stress?',
    'What log or file evidence is missing?',
  ],
  map: [
    'Where are the wells clustered?',
    'Where is Boswell relative to Bald Prairie?',
    'What acreage layers are on the map?',
    'How many wells are plotted?',
  ],
  land: [
    'How HBP is the acreage?',
    'Who buys the gas and how is it gathered?',
    'What ops staff transfers?',
    'Any non-HBP expirations?',
  ],
  sensitivity: [
    'Does the seller forecast assume acid uplift holds?',
    'What does the DCA fade-to-trough case do to package PV?',
    'What happens to value if Boswell fades 25%?',
    'Walk me through the midstream fee stack.',
  ],
  geoscience: [
    'Which wells have open-hole quad combo LAS?',
    'Does Boswell have any LAS in the VDR?',
    'What curves are on TXU 9-1?',
    'How should we use Bald Prairie logs vs Boswell DCA for the bid?',
  ],
  diligence: [
    "What's on the P0 checklist?",
    "What's still missing from the VDR?",
    "What's the multiple back-check range?",
    'Who do I contact at RedOaks?',
  ],
  abs: [
    'What is the illustrative max purchase price and binding constraint?',
    'Walk the advPv10 / advPp → DSCR → note size trade-off.',
    'What happens to coverage if coupon or tenor moves?',
    'Summarize this capital-tape desk for an IC readout.',
  ],
  admin: [
    'What can admins do in this portal?',
    'How does invite / 2FA work for VDR users?',
    'Where should deal-desk users start?',
  ],
  crm: [
    'Research this investor — wealth origin, energy appetite, and a Firebrand hook.',
    'Draft a personalized teaser email for the selected contact.',
    'Review my outreach draft — tighten specificity and CA hygiene.',
    'What should we ask for as the next step (call vs portal invite)?',
  ],
  projects: [
    'Where should I start today?',
    'Remind me which deal I was on last.',
    'Summarize what changed since my last login (CA-safe).',
    'Help me capture a new VDR portal for onboard.',
  ],
};

const engines = {
  executive(d, q) {
    if (/summary|top-line|overview/i.test(q)) {
      return [
        `**Loaded deal package** (Eagle East TX / ETXENERGY · RedOaks) — deal context, not product name.`,
        `PDP PV10 **${formatCurrency(d.kpis.pdpPv10MM * 1e6)}** · NTM CF **${formatCurrency(d.kpis.ntmPdpCfMM * 1e6)}** · Aug prod **${formatInt(d.kpis.augProdMcfpd)} Mcfpd** · **${formatInt(Math.round(d.kpis.netAcres))}** net acres (${formatPct(d.kpis.hbpPct * 100)} HBP).`,
        `Bids due **${d.meta.bidsDue}** noon CT · effective **${d.meta.effectiveDate}**.`,
      ].join('\n\n');
    }
    if (/boswell|concentrat/i.test(q)) {
      return [
        `**Boswell concentration:** ${formatPct(d.concentration.boswellPv10Share * 100, 0)} of PDP PV10 and ${formatPct(d.concentration.boswellNetShare * 100, 0)} of ${d.meta?.primaryProduct === 'oil' ? 'net oil' : 'net gas'}.`,
        `Top 3 wells = ${formatPct(d.concentration.top3Pv10Share * 100, 0)} of PDP PV10.`,
        `Top wells: ${d.topWells
          .slice(0, 3)
          .map((w) => `${w.lease} ($${w.pv10M.toFixed(0)}M)`)
          .join(', ')}.`,
      ].join('\n\n');
    }
    if (/risk/i.test(q)) {
      return [
        `Three priority risks:`,
        ...d.risks.map((r, i) => `**${i + 1}. ${r.title}** (${r.severity}) — ${r.summary}`),
      ].join('\n\n');
    }
    if (/timeline|bid|process|deadline/i.test(q)) {
      return [
        `Process milestones:`,
        ...d.process.map((p) => `• **${p.date}** — ${p.label}`),
      ].join('\n');
    }
    return `Loaded package: ${formatCurrency(d.kpis.pdpPv10MM * 1e6)} PDP PV10, ${formatCurrency(d.kpis.ntmPdpCfMM * 1e6)} NTM CF. Ask about Boswell, risks, or the bid timeline.`;
  },

  reserves(d, q) {
    if (/pbp|behind.?pipe|pdp vs/i.test(q)) {
      return [
        `**PDP:** ${formatCurrency(d.summary.pdpPv10MM * 1e6)} PV10 · ${d.summary.pdpNetBcf} Bcf net.`,
        `**PBP:** ${formatCurrency(d.summary.pbpPv10MM * 1e6)} PV10 · ${d.summary.pbpNetBcf} Bcf · ~$35K/well capex.`,
        `PBP barely moves package value (~2% of PV10).`,
      ].join('\n\n');
    }
    if (/price|deck|differential|shrink/i.test(q)) {
      return [
        `Price deck (HH): ${d.priceDeck.map((p) => `${p.year} $${p.hh}`).join(' · ')}.`,
        `Differentials: ${d.differentials.map((x) => `${x.group} $${x.gasDiff}/Mcf (${x.shrinkPct}% shrink)`).join('; ')}.`,
      ].join('\n\n');
    }
    if (/dominat|top well|pv10|boswell/i.test(q)) {
      const top = d.wellsByPv10.slice(0, 5);
      return [
        `Top wells by PV10:`,
        ...top.map((w, i) => `${i + 1}. **${w.lease}** (${w.resCat}) — $${w.pv10M.toFixed(1)}M · ${formatInt(w.netGasMmcf)} MMcf net`),
      ].join('\n');
    }
    if (/15|20|sensitiv|discount/i.test(q)) {
      const p15 = d.pvSensitivity.find((x) => x.rate === 15);
      const p20 = d.pvSensitivity.find((x) => x.rate === 20);
      return `At 10% PDP PV10 is $${d.summary.pdpPv10MM}MM. At 15%: $${p15.pdpMM}MM. At 20%: $${p20.pdpMM}MM.`;
    }
    return `Reserves: PDP $${d.summary.pdpPv10MM}MM PV10 / ${d.summary.pdpNetBcf} Bcf. Ask about PBP, differentials, or discount sensitivity.`;
  },

  cashflow(d, q) {
    if (/l12|ntm|trailing|compare/i.test(q)) {
      return `L12M NOCF **${formatCurrency(d.kpis.l12Nocf)}** vs seller NTM PDP CF **${formatCurrency(d.kpis.ntmPdpCfMM * 1e6)}** — broadly consistent. L6M avg NOCF ${formatCurrency(d.kpis.l6AvgNocf)}/mo.`;
    }
    if (/realiz|price|differential|hh|henry/i.test(q)) {
      return `L12M avg realized after deducts **$${d.kpis.avgRealized.toFixed(2)}/Mcf** vs HH strip ~$3.42+. Midstream fee stack (Marabou gathering + Enbridge) drives the gap. Model diffs: Bald Prairie −$1.22, Boswell −$1.93.`;
    }
    if (/overhead|g&a|ga\b/i.test(q)) {
      const avgOh = d.kpis.l12AvgOverhead ?? d.kpis.monthlyOverhead;
      return [
        `Seller Overhead/G&A is a **single Net LOS line** (~${formatCurrency(avgOh)}/mo L12 avg; model lump ${formatCurrency(d.kpis.monthlyOverhead)}/mo).`,
        `It is **not** the LOE category mix. Decision desk can toggle/adjust the lump $/mo; per-category buyer knobs are tracked in #89.`,
      ].join('\n\n');
    }
    if (/loe|opex|operating|cost/i.test(q)) {
      return [
        `L6M avg LOE ~${formatCurrency(d.kpis.l6AvgLoe)}/mo (~$3K/well).`,
        `Mix: ${d.loeMix.map((x) => `${x.category} ${formatPct(x.pct * 100, 0)}`).join(' · ')}.`,
        `Seller overhead ~${formatCurrency(d.kpis.monthlyOverhead)}/mo is shown separately (not LOE) — confirm PSA treatment.`,
      ].join('\n\n');
    }
    if (/volatil|month|swing/i.test(q)) {
      const best = [...d.months].sort((a, b) => b.nocf - a.nocf)[0];
      const worst = [...d.months].sort((a, b) => a.nocf - b.nocf)[0];
      return `Best month ${best.month}: ${formatCurrency(best.nocf)} @ $${best.realizedPrice}/Mcf. Worst ${worst.month}: ${formatCurrency(worst.nocf)} @ $${worst.realizedPrice}/Mcf.`;
    }
    return `Cash flow: L12M NOCF ${formatCurrency(d.kpis.l12Nocf)}. Ask about realized price, LOE, or monthly volatility.`;
  },

  wells(d, q) {
    if (/status|pdp|pdsi|p&a|count/i.test(q)) {
      return `Master list: **${d.kpis.pdp} PDP**, **${d.kpis.pdsi} PDSI**, **${d.kpis.pa} P&A**. ${d.kpis.horizontal} horizontal / ${d.kpis.vertical} vertical among PDP. ${d.kpis.modeled} cases in the seller forecast (incl. PBP).`;
    }
    if (/negative|uneconom/i.test(q)) {
      return [
        `${d.negativePv.length} PDP wells show negative PV10 at strip:`,
        ...d.negativePv.slice(0, 8).map((w) => `• ${w.lease}: $${w.pv10M.toFixed(2)}M`),
      ].join('\n');
    }
    if (/behind|pbp|recomplet/i.test(q)) {
      return [
        `Five modeled PBP recompletes (~$35K/well):`,
        ...d.pbpCandidates.map((w) => `• **${w.well}** — ${w.zones} · PV10 $${w.pv10M.toFixed(0)}K · ${formatInt(w.netMmcf)} MMcf`),
      ].join('\n');
    }
    if (/boswell/i.test(q)) {
      const b = d.topProducers.find((w) => /BOSWELL/i.test(w.lease));
      return b
        ? `**Boswell 1** — WI ${b.wi}% / NRI ${b.nri}% · ${formatInt(b.netGasMmcf)} MMcf net remaining · PV10 $${b.pv10M.toFixed(0)}M. Completed 2008, 73 Bcf EUR, operated by Shelby Boswell. Apr 2026 acid job lifted rates.`
        : 'Boswell is the package anchor well.';
    }
    return `Well inventory: ${d.kpis.pdp} PDP. Ask about Boswell, negative-PV wells, or PBP candidates.`;
  },

  land(d, q) {
    if (/hbp|acre|leasehold/i.test(q)) {
      return `**${formatInt(Math.round(d.kpis.netAcres))}** net / **${formatInt(Math.round(d.kpis.grossAcres))}** gross acres · **${formatPct(d.kpis.hbpPct * 100)} HBP**. Non-HBP ~${formatInt(Math.round(d.kpis.nonHbpNet))} net acres expire 2027.`;
    }
    if (/gas|market|gather|marabou|enbridge|midstream/i.test(q)) {
      return [
        `Purchaser: **${d.marketing.purchaser}**.`,
        `${d.marketing.gathering}.`,
        `Boswell: ${d.marketing.boswell}. Bald Prairie: ${d.marketing.baldPrairie}.`,
        `Transport: ${d.marketing.transport}.`,
      ].join('\n\n');
    }
    if (/staff|ops|operator|transfer/i.test(q)) {
      return `${d.ops.fieldStaff}. Compression: ${d.ops.compressors}. SWD: ${d.ops.swd}.`;
    }
    if (/expir|non.?hbp|2027/i.test(q)) {
      return `~${formatInt(Math.round(d.kpis.nonHbpNet))} net acres are non-HBP and expire in 2027 — small vs total but worth calendarizing.`;
    }
    return `Land: ${formatInt(Math.round(d.kpis.netAcres))} net acres, ${formatPct(d.kpis.hbpPct * 100)} HBP. Ask about midstream, ops staff, or expirations.`;
  },

  sensitivity(d, q) {
    if (/dca|fade.?to.?trough|challenger|trough.?start|hold.?post.?acid/i.test(q)) {
      const live = d.livePageState;
      return [
        `**Boswell DCA challenger** compares seller qi **${d.boswell.ariesQi.toLocaleString()} Mcf/d** to post-acid ~**${formatInt(d.boswell.postAcidWeekly)}** and Jan–Mar trough ~**${formatInt(d.boswell.troughMcfpd)}**.`,
        live?.dcaVerdict ||
          'If rates fade toward the trough, package PV is overstated versus the seller forecast — Apply Fade/Trough on Sensitivity to set Boswell vol adj.',
        live?.dcaCase ? `Active DCA case on desk: **${live.dcaCase}** (vol ${(Number(live.boswellVolAdj || 0) * 100).toFixed(0)}%).` : '',
      ]
        .filter(Boolean)
        .join('\n\n');
    }
    if (/aries|acid|forecast|qi|uplift/i.test(q)) {
      return `The seller forecast starts Boswell at **${d.boswell.ariesQi.toLocaleString()} Mcf/d**. That matches post-acid rates (~${d.boswell.postAcidWeekly.toLocaleString()} weekly), not the Jan–Mar trough (~${d.boswell.troughMcfpd.toLocaleString()} Mcf/d). The model capitalizes acid uplift.`;
    }
    if (/25|fade|boswell.*value|pv/i.test(q)) {
      const s = d.bidScenarios.find((x) => x.id === 'bos25');
      const base = d.bidScenarios.find((x) => x.id === 'base');
      const pv = s?.pv ?? s?.pv10;
      return `Boswell −25% case: adj PV **$${pv}MM**, adj NTM **$${s?.ntm}MM**, 5× mark **$${s?.x5}MM** (vs base $${base?.x5}MM).`;
    }
    if (/midstream|fee|stack|gather|demand/i.test(q)) {
      return `Illustrative stack ~$${d.midstream.stackAt6k}/MMBtu at 6k MMBtu/d (${d.midstream.stack.map((x) => `${x.component} $${x.unit}`).join('; ')}). Firm demand alone rises to $0.44 @ 4k and $0.58 @ 3k.`;
    }
    if (/bear|base|bid|5×|5x/i.test(q)) {
      const base = d.bidScenarios.find((x) => x.id === 'base');
      const bear = d.bidScenarios.find((x) => x.id === 'bear');
      return `Base 5× = **$${base.x5}MM**. Bear (Boswell −25% & HH −$0.50) 5× = **$${bear.x5}MM**. Price moves dominate in this thin-netback package.`;
    }
    return `Sensitivity covers Boswell acid/decline, midstream stack, and bid marks. Ask about seller qi, −25% Boswell, or bear vs base.`;
  },

  geoscience(d, q) {
    if (/boswell/i.test(q)) {
      return `**Boswell has ${d.boswellLasCount ?? 0} LAS** in the VDR. Geology diligence for the value well is completion/ops/RRC — not logs. Use the **Sensitivity DCA challenger** for qi risk; use this tab for Bald Prairie secondaries.`;
    }
    if (/quad|open.?hole|combo/i.test(q)) {
      const quads = (d.files || []).filter((f) => f.kind === 'openhole-quad');
      if (!quads.length) return 'No open-hole quad combo LAS indexed.';
      return `Open-hole quad LAS:\n${quads.map((f) => `• **${f.wellFolder}** — ${f.fileName} (${(f.preferred || []).join(', ')})`).join('\n')}`;
    }
    if (/txu\s*9|9-1/i.test(q)) {
      const hits = (d.files || []).filter((f) => /9-1|9 #1/i.test(f.wellFolder + f.fileName));
      return hits.length
        ? `TXU 9-1 LAS (${hits.length}):\n${hits.map((f) => `• ${f.fileName} — ${f.kind} · ${(f.preferred || []).join(', ') || 'meta only'}`).join('\n')}`
        : 'No TXU 9-1 LAS in the index.';
    }
    if (/how|bid|value|dca|vs/i.test(q)) {
      return `**${d.fileCount} LAS / ${d.wellCount} wells** — useful for secondary geology (GR, RHOB, NPHI, RT on quads). Package bid edge remains **Boswell DCA + netback**, not an earth model from these logs. ${d.dcaHint || ''}`;
    }
    return `Geoscience inventory: **${d.fileCount}** LAS across **${d.wellCount}** wells. Ask about quads, Boswell coverage, or a well name.`;
  },

  map(d, q) {
    if (/boswell/i.test(q)) {
      if (!d.boswell) return 'Boswell location is not in the map payload.';
      return `**Boswell** plots at **${d.boswell.lat.toFixed(4)}°, ${d.boswell.lng.toFixed(4)}°** — southeast of the main Bald Prairie cluster. Marker size reflects its outsized PV10.`;
    }
    if (/cluster|where|location|geography|county/i.test(q)) {
      return [
        `**${formatInt(d.kpis.wellsPlotted)}** wells plotted (RRC surface holes).`,
        `Main Bald Prairie cluster is Robertson County around ~31.15–31.25°N / −96.52–−96.57°W; Boswell sits southeast.`,
        `Acreage: **${formatInt(d.kpis.drillingUnits)}** drilling units + **${formatInt(d.kpis.leaseTracts)}** lease tracts from 2021 VDR shapefiles.`,
      ].join('\n\n');
    }
    if (/acre|layer|shape|unit|tract/i.test(q)) {
      return [
        `Map layers:`,
        `• **Lease tracts** — ${formatInt(d.kpis.leaseTracts)} polygons (status-colored)`,
        `• **Drilling units** — ${formatInt(d.kpis.drillingUnits)} Bald Prairie unit outlines`,
        `• **Wells** — PDP (green), PDSI (amber), P&A (gray); size ≈ PV10`,
        ...(d.notes || []).map((n) => `• ${n}`),
      ].join('\n');
    }
    if (/how many|count|plotted|kpi/i.test(q)) {
      return `${formatInt(d.kpis.wellsPlotted)} wells (${d.kpis.pdp} PDP / ${d.kpis.pdsi} PDSI / ${d.kpis.pa} P&A) over ~${formatInt(Math.round(d.kpis.netAcres))} net acres.`;
    }
    return `Map tab shows all ${formatInt(d.kpis.wellsPlotted)} master-list wells with VDR acreage polygons. Ask about Boswell, clusters, or layers.`;
  },

  diligence(d, q) {
    if (/p0|checklist|diligence list/i.test(q)) {
      const p0 = d.checklist.filter((c) => c.priority === 'P0');
      return [`**P0 items:**`, ...p0.map((c) => `• ${c.item} _(owner: ${c.owner})_`)].join('\n');
    }
    if (/missing|vdr|gap|complet/i.test(q)) {
      const miss = d.vdrStatus.filter((v) => /Missing|Partial/i.test(v.status));
      return [`Still open in the VDR:`, ...miss.map((v) => `• **${v.section}** (${v.status}) — ${v.note}`)].join('\n');
    }
    if (/bid|valu|band|multiple|price/i.test(q)) {
      const low = d.valuation.multipleBackcheckLowMM ?? d.valuation.illustrativeLowMM;
      const high = d.valuation.multipleBackcheckHighMM ?? d.valuation.illustrativeHighMM;
      return `${d.quote}\n\nAnchors: PDP PV10 ${formatCurrency(d.valuation.pdpPv10MM * 1e6)}, NTM CF ${formatCurrency(d.valuation.ntmCfMM * 1e6)}, L12M NOCF ${formatCurrency(d.valuation.l12NocfMM * 1e6)}. Multiple back-check ${formatCurrency(low * 1e6)}–${formatCurrency(high * 1e6)}. ${d.valuation.note}`;
    }
    if (/contact|redoak|who/i.test(q)) {
      return d.contacts.map((c) => `• **${c.name}** — ${c.role} · ${c.email}`).join('\n');
    }
    const low = d.valuation.multipleBackcheckLowMM ?? d.valuation.illustrativeLowMM ?? 0;
    const high = d.valuation.multipleBackcheckHighMM ?? d.valuation.illustrativeHighMM ?? 0;
    return `Diligence tab covers P0 checklist, VDR gaps, midstream assignability, and a ${formatCurrency(low * 1e6)}–${formatCurrency(high * 1e6)} multiple back-check.`;
  },

  decision(d, q) {
    const live = d?.livePageState;
    if (/seller ask|asking price|seller's ask/i.test(q)) {
      return localSellerAskAnswer(d);
    }
    if (/note size|note sizing|max note|how large a note|how big a note/i.test(q)) {
      return localNoteSizingAnswer(d);
    }
    if (/irr|bid|\$13|return/i.test(q) && live) {
      const irr =
        live.baseIrr == null ? 'n/m' : `${(live.baseIrr * 100).toFixed(1)}%`;
      const stress =
        live.stressIrr == null ? 'n/m' : `${(live.stressIrr * 100).toFixed(1)}%`;
      return [
        `At your live desk bid **$${Number(live.bidPriceMM).toFixed(1)}MM**:`,
        `• Base IRR **${irr}** · stress (desk) IRR **${stress}**`,
        `• Gross ${live.pvLabel || 'PV'} **$${Number(live.pvMM).toFixed(2)}MM** · after ARO **$${Number(live.bidBridgePvMM).toFixed(2)}MM**`,
        `• Boswell share **${(Number(live.boswellShare) * 100).toFixed(0)}%**`,
        `Working signal: **${live.recommendation || '—'}**.`,
      ].join('\n');
    }
    if (/concentrat|boswell/i.test(q)) {
      const share = live?.boswellShare ?? d?.boswellShare;
      const shareLabel = share != null ? `${(share * 100).toFixed(0)}%` : '~59%';
      return `Boswell is **${shareLabel}** of modeled PDP PV — price as a single-well-dominated package, not diversified PDP. Disclose concentration before returns in any IC/LP pack.`;
    }
    if (/stress|ceiling|downside/i.test(q)) {
      return `Desk stress locks HH −$0.50 · Boswell −25% · netback −$0.15 · overhead in CF (not the same as packaged named \`bear\`). Use stress IRR at your bid as the downside ceiling check; save a named scenario before circulating Word/Excel.`;
    }
    if (/bridge|aro|price/i.test(q)) {
      return `Bid bridge: Gross model PV → − ARO (judgment) → equity/asset PV → your bid → IRR/MOIC. Multiples are a back-check only. ${localSellerAskAnswer(d)}`;
    }
    if (/letter|redoaks|submit|proposal|financing|letterhead/i.test(q)) {
      return [
        '**RedOaks bid submission** (separate from LP capital packs):',
        '• Written proposal on company letterhead, emailed to David Carter (cc Heather Agnew, Eli Garcia).',
        '• Due **Aug 5, 2026 · 12:00pm CST** · effective **Aug 1** · remain open through **at least Aug 14**.',
        '• Required sections: financing/contingencies, validity, clarification contacts + purchase price.',
        '• On this desk: fill the **RedOaks bid submission** panel → **Bid letter (.docx)** → PDF on letterhead → send.',
      ].join('\n');
    }
    return `Decision desk: set bid/knobs, read IRR (nominal annual, monthly compounding) and desk stress, export the RedOaks bid letter for the advisor, then save named scenarios / Word-Excel for IC. ${localSellerAskAnswer(d)}`;
  },

  admin() {
    return [
      '**Admin** manages VDR portal users (invite, roles, 2FA reset).',
      'Deal-desk users should start on **Decision**, then Sensitivity / Diligence for Boswell, netback, and P0 open items.',
      'All tabs stay CA-gated; the assistant only sees loaded page + DealPackage context.',
    ].join('\n\n');
  },

  abs(d) {
    const tab = d?.livePageState?.absTab || d?.absTab || 'summary';
    return [
      `**ABS / capital-tape desk** (surface tab: ${tab}).`,
      'This is an underwriting handshake desk — never claim the platform is the rated issuer.',
      'Ask about implied max bid, binding constraint, DSCR, advance rate, or coverage sensitivity.',
      d?.surface === 'abs' ? 'Live ABS run context is available when the desk has finished loading.' : '',
    ]
      .filter(Boolean)
      .join('\n\n');
  },

  crm(d, q) {
    const firm = d?.livePageState?.firm || d?.firm;
    const contact = d?.livePageState?.contact;
    const draft = d?.livePageState?.outreachDraft;
    const name = firm?.firm_name || 'the selected firm';
    if (/research|hook|appetite|origin/i.test(q)) {
      if (!firm) {
        return 'Select a firm in the CRM list first — I need FINTRX fields (tier, wealth origin, fit thesis, contacts) before researching.';
      }
      return [
        `**Local research brief — ${name}** (Claude + web search unavailable; using CRM fields only)`,
        `• **Tier / type:** ${firm.tier || '—'} · ${firm.firm_type || '—'} · ${firm.hq_city || ''}, ${firm.hq_state || ''}`,
        `• **AUM:** ${firm.aum || '—'}`,
        `• **Wealth origin:** ${firm.wealth_origin || '—'}`,
        `• **Energy / real-asset evidence:** ${firm.energy_evidence || '—'}`,
        `• **Example deals:** ${firm.example_deals || '—'}`,
        `• **Fit thesis:** ${firm.fit_rationale || '—'}`,
        `• **Hook:** Lead with their ${firm.wealth_origin ? 'wealth-origin story' : 'direct-investing posture'} and Eagle as conventional East TX PDP equity (illustrative unlevered asset-underwriting case; financing none assumed) — ask for a short call or portal invite.`,
      ].join('\n');
    }
    if (/review|tighten|hygiene/i.test(q)) {
      if (!draft?.body) {
        return 'Open a firm and load the outreach draft, then ask me to review it.';
      }
      return [
        '**Local draft review** (heuristics only — Claude unavailable):',
        draft.subject ? `• Subject length ${draft.subject.length} chars${draft.subject.length > 90 ? ' — consider shortening' : ''}.` : '• Missing subject.',
        /IRR|PV10|\$\d/i.test(draft.body)
          ? '• Mentions numbers — confirm they are investor-safe (no seller ask / calibration).'
          : '• No hard numbers detected — fine for a teaser.',
        !/portal|call|chat|meet/i.test(draft.body)
          ? '• Add a soft next step (brief call or portal invite).'
          : '• Next-step language present.',
        `• Personalization: ${firm?.fit_rationale ? 'fit thesis available — ensure it appears in the body' : 'no fit thesis on file'}.`,
      ].join('\n');
    }
    if (/draft|email|outreach|teaser|write/i.test(q)) {
      if (!firm) {
        return 'Select a firm first so I can personalize the teaser.';
      }
      const first = (contact?.name || 'there').split(/\s+/)[0] || 'there';
      const thesis = (firm.fit_rationale || '').trim();
      const subject = `Firebrand Energy — Eagle East TX equity for ${name}`;
      const body = [
        `Hi ${first},`,
        '',
        'I am reaching out from Firebrand Energy about a conventional East Texas operated gas PDP equity opportunity (Eagle) — an illustrative unlevered asset-underwriting / acquisition-equity case. Financing status: none assumed.',
        '',
        thesis
          ? `Why we thought of ${name}: ${thesis}`
          : `Given ${name}'s energy / real-asset posture, we wanted to share a short capital one-pager.`,
        '',
        'Happy to walk through fit on a brief call, or I can send private investor-portal access.',
        '',
        'Best regards',
      ].join('\n');
      return `**Subject:** ${subject}\n\n**Body:**\n${body}`;
    }
    return 'On Investor CRM I can research the selected firm, draft a personalized teaser, or review your outreach. Select a firm, then use a suggested prompt.';
  },

  projects(d, q) {
    const deals = Array.isArray(d?.deals) ? d.deals : [];
    const names = deals.map((x) => x.label || x.dealId).filter(Boolean);
    const list = names.length ? names.join(', ') : 'Eagle, Juno, Sedgwick';
    if (/last|left off|resume|where was/i.test(q)) {
      return [
        '**Where you left off** is stored per browser user (last deal + route).',
        `Use the briefing CTAs above, or pick a desk from: ${list}.`,
        'I will not invent economics — open Overview after selecting a deal for package-backed facts.',
      ].join('\n\n');
    }
    if (/update|since|login|changed/i.test(q)) {
      return [
        '**Since last login (CA-safe):** desk session metadata only — no package change feed is wired yet.',
        `Available registry desks: ${list}.`,
        'Open Diligence / Overview on a selected deal for grounded status; do not treat this chat as a valuations update.',
      ].join('\n\n');
    }
    if (/vdr|portal|onboard|new project|create/i.test(q)) {
      return [
        'Admins can open **Create new project** to capture VDR portal URL, room name, entitled email, auth method (never raw passwords), and expected packages.',
        'Drafts save under ENERGY_AUTH_DATA via the admin API (or local export fallback).',
      ].join('\n\n');
    }
    return [
      '**Select project** — choose a deal desk to drive Overview, Decision, ABS, and package JSON.',
      `Desks: ${list}.`,
      'Ask where to resume, what is CA-safe since last login, or how to capture a VDR portal for onboard.',
    ].join('\n\n');
  },
};

/** Parse assistant markdown into outreach subject/body for CRM insert. */
export function parseOutreachDraft(text) {
  if (!text || typeof text !== 'string') return null;
  const subjectMatch = text.match(/\*\*Subject:\*\*\s*(.+?)(?:\n|$)/i) || text.match(/^Subject:\s*(.+)$/im);
  const bodyMatch = text.match(/\*\*Body:\*\*\s*\n([\s\S]+)/i) || text.match(/^Body:\s*\n([\s\S]+)/im);
  if (!subjectMatch && !bodyMatch) return null;
  let body = (bodyMatch?.[1] || '').trim();
  // Drop trailing Sources section if present
  body = body.replace(/\n\*\*Sources\*\*[\s\S]*$/i, '').trim();
  const subject = (subjectMatch?.[1] || '').replace(/\*\*/g, '').trim();
  if (!subject && !body) return null;
  return { subject: subject || 'Firebrand Energy — Eagle East TX', body };
}

export function generateLocalAnswer({ dashboardKey, data, question, context }) {
  const page = context?.page ?? data;
  const live = context?.livePageState;
  const enriched =
    page || live || context?.deskBid || context?.sellerAskMM != null
      ? {
          ...(page || {}),
          livePageState: live,
          sellerAskMM: context?.sellerAskMM ?? page?.sellerAskMM ?? null,
          deskBid: context?.deskBid ?? null,
          boswellShare: page?.boswellShare,
        }
      : null;
  if (/seller ask|asking price|seller's ask/i.test(question || '')) {
    return localSellerAskAnswer(enriched || {});
  }
  if (/note size|note sizing|max note|how large a note|how big a note/i.test(question || '')) {
    return localNoteSizingAnswer(context);
  }
  if (!enriched && dashboardKey !== 'admin' && dashboardKey !== 'crm' && dashboardKey !== 'projects') {
    return 'Page data still loading — open again in a moment, or ask a general deal question after the tab finishes loading.';
  }
  const engine = engines[dashboardKey];
  if (!engine) {
    return `No local fallback for **${dashboardKey}** yet — retry when Claude is available, or switch to Decision / Diligence.`;
  }
  if (!question) return engine(enriched || {}, 'summary');
  return engine(enriched || {}, question);
}

export { renderMarkdown, escapeHtml } from './markdown.js';

export { parseSseDataLine, readAssistantSse } from './assistantSse.js';
